import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { getLocale, useIntl, useDispatch, useSelector } from 'umi';
import PerformanceMetricsTable, { MetricsTableColumn, PagingQuery, SortBy } from '@/pages/dashboard/components/PerformanceMetricsTable';

import { render as renderFun } from './theads';
import {
  getDynamicLocalCnEnText,
  formatFilterValues,
  getDateWeekDayStr,
  generateCopyData,
  formatAccumulation,
} from '@/pages/dashboard/utils/utils';
import { QUERY_PERIOD } from '@/pages/dashboard/pages/monetization/_config/constant';
import { useSelectArrObj, useLocal } from '@/pages/dashboard/utils/customHook';
import { getOverallPerformanceDownLoad } from "@/pages/dashboard/services/monetization";
import useRevenueOverallPaymentModel from '@/pages/dashboard/servicesModels/monetization/revenueOverallPaymentModel';
import { downloadFileWithFileFormat, useNotificationOfDownloadTable } from "@/pages/dashboard/components/DownloadFormatModal";
import { getMonetUnit } from '@/pages/dashboard/utils/moneyUnit';
import DimensionAccumulateRow from '@/pages/dashboard/components/DimensionAccumulateRow';
import { Bubble } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { getExplainVal } from '@/pages/dashboard/utils/getExplainVal';

const QUERY_DATE_FORMAT = 'YYYYMMDD';
const DEFAULT_SELECTED = ['date'];
const DEFAULT_SORTS: SortBy[] = []; // { by: 'date', order: 'desc' }
const DEFAULT_PAGING: PagingQuery = { pageIndex: 1, pageSize: 10 };
const widthLarge = 220 - 20;
const widthMid = 205 - 20;

export default function PerformanceTable(props: any) {
  const { gameCode, explainInfo, enName, cnName, isShowDownload, isShowResizeEnd } = props;
  const contextObj = useContext(FilterContext);
  const { filterVal: formValue, filterLoading } = contextObj || {};
  const [filterConfirm, setFilterConfirm] = useState(false);
  const { isEN } = useLocal();
  const { selector } = useSelector(state => state?.dashboardSetting ?? {});
  const timeType = formValue?.timeType || 'Day';
  const { formatMessage } = useIntl();
  const locale = getLocale();

  function getGroupCols(selected: string[]): MetricsTableColumn<any>[] {
    const th: MetricsTableColumn<any>[] = [];
    const headText = {
      'Zone': formatMessage({ id: 'common_Zone' }),
      'Region': formatMessage({ id: 'common_Region' }),
      'Country': formatMessage({ id: 'common_Country' }),
      'System': formatMessage({ id: 'common_System' }),
      'Channel': formatMessage({ id: 'common_Channel' }),
      'Language': formatMessage({ id: 'common_Language' }),
      'Bundle': formatMessage({ id: 'common_Bundle' }),
    };
    selected.filter(ele => ele !== 'date').forEach(ele => {
      const keyHead = ele.replace(ele[0], ele[0].toLocaleUpperCase());
      th.push({
        key: ele,
        header: headText?.[keyHead],
        width: 200,
        align: 'left',
        render: (record) => {
          return (<span className='db-PerformanceMetricsTable__text'>{getDynamicLocalCnEnText(ele, record[ele], gameCode, getLocale(), selector)}</span>);
        }
      });
    });
    return th;
  }

  function getColumns(
    timeType: string,
    groups: string[] = [],
    explainInfo: any = {}
  ): MetricsTableColumn<any>[] {
    const onlyHeaders = {
      Day: [
        { key: '30d_ltv', header: formatMessage({ id: 'ua_LTV30' }), rainbowKey: 'LTV30', width: 200, format: '0.00' },
        { key: '180d_ltv', header: formatMessage({ id: 'ua_LTV180' }), rainbowKey: 'LTV180',  width: 200, format: '0.00' },
      ],
      Week: [
        // { key: 'register_payer', header: 'Register Payer', width: 200, format: '0', },
      ],
      Month: [
      ]
    };
    const metrics = {
      Day: formatMessage({ id: 'monetization_revenue_DAU' }),
      Week: formatMessage({ id: 'monetization_revenue_WAU' }),
      Month: formatMessage({ id: 'monetization_revenue_MAU' }),
    };
    const metricsExplain = {
      Day: 'DAU',
      Week: 'WAU',
      Month: 'MAU',
    };
    let dateCol: MetricsTableColumn<any> = {
      key: 'date',
      header: formatMessage({ id: 'monetization_revenue_Date' }),
      width: 160,
      align: 'left',
      fixed: 'left',
      render: (record) => {
        /** Sum提示需要用到useState，
        *所以拿出来单独处理，
        *不然render里拿不到最新的的state
        */
        return <DateCell record={record} formatMessage={formatMessage} />;
      },
    };
    // 日期列, 周六日的颜色高亮 & Sum行增加提示
    function DateCell({ record, formatMessage }) {
      const [isIconHover, setIsIconHover] = useState(false);
      const locale = getLocale();
      const { weekStr, isSunOrSat } = getDateWeekDayStr(record.date, formatMessage);
      const showSumTip = record.date === 'Sum' || record.date === '汇总值';

      return (
        <div style={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>
          <span>{record.date}</span>
          {timeType === 'Month' ? <span style={{ display: 'inline-block', width: '0.4em' }}></span> :
            <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekStr}</span>
          }
          {showSumTip && (
            <div className='port-item'>
              <div onMouseOver={() => { setIsIconHover(true) }} onMouseOut={() => { setIsIconHover(false) }} >
                <Bubble
                  dark
                  placement="top-start"
                  arrowPointAtCenter
                  style={{ maxWidth: locale === 'en-US' ? '235px' : '253px' }}
                  content={formatMessage({ id: 'overview_sum_tip' })}
                >
                  <div><SvgIcon className='port-icon' type={isIconHover ? 'alert-circle-action' : 'alert-circle-m'} /></div>
                </Bubble>
              </div>
            </div>
          )}
        </div>
      );
    }
    const groupCols: MetricsTableColumn<any>[] = getGroupCols(groups);
    const metricCols: MetricsTableColumn<any>[] = [
      { 
        key: 'total_pay_amount',
        header: formatMessage({ id: 'monetization_revenue_Total_Revenue($)' }, { unit: getMonetUnit() }),
        rainbowKey: 'Total Revenue($)',
        width: widthMid + 55,
        format: '0.00'
      },
      { 
        key: 'pay_amount',
        header: formatMessage({ id: 'monetization_revenue_Revenue($)' }, { unit: getMonetUnit() }),
        rainbowKey: 'Revenue($)',
        width: widthMid + 50,
        format: '0.00'
      },
      { 
        key: 'active_users',
        header: metrics?.[timeType],
        width: widthMid + 20,
        rainbowKey: metricsExplain?.[timeType],
        format: '0'
      },
      { 
        key: 'arppu',
        header: formatMessage({ id: 'monetization_revenue_ARPPU($)' }, { unit: getMonetUnit() }),
        rainbowKey: 'ARPPU($)',
        width: widthMid,
        format: '0.00'
      },
      { 
        key: 'arpu',
        header: formatMessage({ id: 'monetization_revenue_ARPU($)' }, { unit: getMonetUnit() }),
        rainbowKey: 'ARPU($)',
        width: widthMid,
        format: '0.00'
      },
      { 
        key: 'paid_users',
        header: formatMessage({ id: 'monetization_revenue_Payer' }),
        rainbowKey: 'Payer',
        width: widthMid + 10,
        format: '0'
      },
      { 
        key: 'payer_rate',
        header: formatMessage({ id: 'monetization_revenue_Payer%' }),
        rainbowKey: 'Payer%',
        width: widthMid,
        format: '0.00%'
      },
      { 
        key: 'arpp',
        header: formatMessage({ id: 'monetization_revenue_ARPP' }),
        rainbowKey: 'ARPP',
        width: widthMid,
        format: '0.00'
      },
      { 
        key: 'new_payer_rate',
        header: formatMessage({ id: 'monetization_revenue_Payer%(new users)' }),
        rainbowKey: 'Payer%(new users)',
        width: locale === 'en-US' ? widthLarge + 40 : widthLarge + 50,
        format: '0.00%'
      },
      { 
        key: 'new_paid_users',
        header: formatMessage({ id: 'monetization_revenue_New_Payer' }),
        rainbowKey: 'First-time Payer',
        width: widthMid,
        format: '0'
      },
      { 
        key: 'new_pay_amount',
        header: formatMessage({ id: 'monetization_revenue_Revenue(new_users)' }),
        rainbowKey: 'Revenue(first-time payer)',
        width: widthLarge + 60,
        format: '0.00'
      },
      { 
        key: 'new_arppu',
        header: formatMessage({ id: 'monetization_revenue_New_users_ARPPU($)' }, { unit: getMonetUnit() }),
        rainbowKey: 'First-time Payer ARPPU($)',
        width: widthLarge + 76,
        format: '0.00'
      },
      { 
        key: 'new_user_arpu',
        header: formatMessage({ id: 'monetization_revenue_New_users_ARPU($)' }, { unit: getMonetUnit() }),
        rainbowKey: 'New users ARPU($)',
        width: widthMid + 50,
        format: '0.00'
      },
      { 
        key: 'total_pay_days',
        header: formatMessage({ id: 'monetization_revenue_Total_Pay_Days' }),
        rainbowKey: 'Total Pay Days',
        width: widthMid + 10,
        format: '0'
      },
      ...onlyHeaders[timeType]
    ];

    metricCols.forEach((col: any) => {
      col.align = 'right';
      col.render = renderFun();

      // 指标说明
      if (explainInfo?.[col.rainbowKey]) {
        col.explain = getExplainVal(explainInfo, col?.rainbowKey, locale);
      }
    });
    return [dateCol, ...groupCols, ...metricCols];
  }

  /* hooks */
  const [selected, setSelected] = useState(DEFAULT_SELECTED);
  const [thead, setThead] = useState<MetricsTableColumn<any>[]>(getColumns(timeType, [], explainInfo));
  const [tdata, setTdata] = useState<any[]>([]);
  const [sorts, setSorts] = useState<any[]>(DEFAULT_SORTS);
  const [paging, setPaging] = useState<PagingQuery>(DEFAULT_PAGING);
  const [pageTotal, setPageTotal] = useState(0);
  const [tableCopyData, setTableCopyData] = useState<any>(null);
  let selectOptions = useSelectArrObj(formValue?.filterValue);
  selectOptions = selectOptions?.filter(i => i?.groupKey === 'separated');
  selectOptions.unshift({
    groupKey: 'separated',
    value: 'date',
    text: formatMessage({ id: 'common_by_Date' }),
    disabled: true,
  });

  const accumulateOldValueRef = useRef(DEFAULT_SELECTED);

  const TITLE = locale === "en-US" ? enName : cnName;
  const { setNoticeStatus } = useNotificationOfDownloadTable(TITLE);
  const NAME_MAP = { sum: formatMessage({ id: 'common_Sum' }), avg: formatMessage({ id: 'common_Average' }) };

  const dispatch = useDispatch();
  const revenueOverallPayment = useRevenueOverallPaymentModel({gameCode}, formValue, dispatch, filterLoading, selected, paging, sorts, QUERY_PERIOD[timeType], filterConfirm);
  const {
    aggregateData,
    componentData,
    error,
    loading,
  } = revenueOverallPayment;

  useEffect(() => {
    if (!componentData) {
      setTdata([]);
      setPageTotal(0);
      setThead([]);
      return;
    }
    const tData: any[] = [];
    const copyData: any[] = [];
    let temp: any;
    componentData?.metrics_list?.forEach((row) => {
      temp = {
        date: moment(row?.date).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD'),
        channel: row?.channel || '-',
        channel_name: row?.channel_name || '-',
        country: row?.country || '-',
        country_name: row?.country_name || '-',
        region: row?.region || '-',
        region_name: row?.region_name || '-',
        system: row?.system || '-',
        system_name: row?.system_name || '-',
        zone: row?.zone || '-',
        zone_name: row?.zone_name || '-',
        language: row?.language || '-',
        language_name: row?.language_name || '-',
        bundle: row?.bundle || '-',
        bundle_name: row?.bundle_name || '-',
      };
      Object.keys(row?.metrics || {}).forEach(k => {
        temp[k] = row?.metrics[k].value;
        temp[k + '_dod'] = row?.metrics[k].dod;
      });
      const generateCpd = generateCopyData(temp, { gameCode, selectorList: selector });
      copyData.push({ ...temp, ...generateCpd });
      tData.push(temp);
    });

    // Sum and Average
    aggregateData?.metrics_list?.forEach(row => {
      temp = {
        date: NAME_MAP[row?.type],
        channel: '-',
        channel_name: '-',
        country: '-',
        country_name: '-',
        region: '-',
        region_name: '-',
        system: '-',
        system_name: '-',
        zone: '-',
        zone_name: '-',
        language: row?.language || '-',
        language_name: row?.language_name || '-',
        bundle: row?.bundle || '-',
        bundle_name: row?.bundle_name || '-',
      };
      Object.keys(row?.metrics || {}).forEach(k => {
        temp[k] = row?.metrics[k].value;
        temp[k + '_dod'] = row?.metrics[k].dod;
      });
      tData.push(temp);
      const generateCpd = generateCopyData(temp, { gameCode, selectorList: selector });
      copyData.push({ ...temp, ...generateCpd });
    });

    setTableCopyData(copyData);
    setTdata(tData);
    setPageTotal(componentData?.row_count || 0);
    setThead(getColumns(timeType, selected, explainInfo));
    setFilterConfirm(false);
  }, [aggregateData, componentData, getLocale()]);

  useEffect(() => {
    if (filterConfirm) {
      setPaging(DEFAULT_PAGING);
      setSorts(DEFAULT_SORTS);
    }
  }, [filterConfirm]);

  function getQS(selected, sorts, query: PagingQuery) {
    const { filterValue } = formValue;
    const { rangeTime, ...filters } = filterValue;
    return {
      period: QUERY_PERIOD[timeType],
      game_code: gameCode,
      start_date: moment(rangeTime?.[0]).format(QUERY_DATE_FORMAT),
      end_date: moment(rangeTime?.[1]).format(QUERY_DATE_FORMAT),
      filters: formatFilterValues(filters),
      accumulation: formatAccumulation(selected),
      page: query.pageIndex,
      page_size: query.pageSize,
      sorted_by: sorts?.[0] ? sorts[0].by : 'date',
      desc: sorts?.[0] ? sorts[0].order === 'desc' : true
    };
  }

  function handleAccumulateChange(list: string[]) {
    setSelected(list);
  }

  function handleSortChange(newSorts: any[]) {
    setSorts(newSorts);
    setFilterConfirm(false);
  }

  function handlePagingChange(query) {
    setPaging(query);
    setFilterConfirm(false);
  }

  function handleDownload() {
    const downloadPromise = downPromise?.();
    if (downloadPromise) { // 有返回Promise对象时由内部处理下载过程
      // 展示正在下载中通知
      setNoticeStatus("loading");
      // 发起请求下载
      (async () => {
        try {
          const res = await downloadPromise;
          const fileName = `${TITLE.replace(/\s/g, '_')} ${moment().format('YYYY_MM_DD HH_mm_ss')}`;
          await downloadFileWithFileFormat(res, fileName, {
            onDownloadSuccess() {
              setNoticeStatus("success");
            },
            onDownloadError() {
              setNoticeStatus("error");
            }
          });
        } catch (e) {
          setNoticeStatus("error");
        }
      })();
    }
  }

  function downPromise() {
    const params = getQS(selected, sorts, paging);
    return getOverallPerformanceDownLoad(params, isEN ? 'en' : 'zh');
  }

  return (
    <PerformanceMetricsTable
      loading={filterLoading || loading}
      copyDataType='dod'
      timeType={timeType}
      isError={error}
      title={TITLE}
      topTip={{
        left: <div className="overall-conext-bottom">
          <DimensionAccumulateRow
            value={selected}
            options={selectOptions}
            onChange={(value) => {
              handleAccumulateChange(value);
              if (accumulateOldValueRef?.current?.toString() !== value?.toString()) {
                accumulateOldValueRef.current = value;
                setPaging({ pageIndex: 1, pageSize: paging.pageSize });
                setSorts(DEFAULT_SORTS);
                setFilterConfirm(false);
              }
            }}
          />
        </div>
      }}
      columns={thead}
      records={tdata}
      copyConfig={
        {
          copyData: tableCopyData,
          copyDataType: 'dod'
        }
      }
      sorting={{
        defaultSorts: sorts,
        onSortChange: handleSortChange
      }}
      paging={{
        ...paging,
        recordCount: pageTotal,
        onPagingChange: handlePagingChange
      }}
      onResizeEnd={isShowResizeEnd ? (cols) => setThead(cols) : undefined}
      onDownload={handleDownload}
      showDownloadBtn={isShowDownload}
    />
  );
}
